import React, { useState, useEffect, useCallback } from 'react';
import { Container, Badge, Button, Table, Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import './CustomerDetail.css';
import api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import { confirm, confirmDelete } from '../components/ConfirmDialog';
import { getStateStyle, formatPrice, formatDate, MultiItemCell, RatecardItemsCell } from '../components/OrderCells';

// Kolik objednávek ukázat v bloku; zbytek v modalu „Zobrazit vše"
const OBJEDNAVKY_V_BLOKU = 5;

// Kolik kontaktních osob ukázat v bloku Lidé; zbytek v modalu „Zobrazit vše"
const LIDE_V_BLOKU = 5;

/**
 * Objednávky zákazníka — stejné sloupce a styly jako výpis objednávek,
 * bez sloupce Klient (je to aktuální zákazník); číslo a datum v jedné buňce.
 */
function ZakaznikObjednavkyTable({ orders, onOpen }) {
  return (
    <div className="table-responsive">
      <Table hover className="align-middle orders-table mb-0">
        <thead className="table-light">
          <tr>
            <th>Objednávka</th>
            <th>Médium</th>
            <th>Projekt</th>
            <th>Inzeráty</th>
            <th>Obchodník</th>
            <th>Stav</th>
            <th className="text-end">Cena</th>
            <th className="text-end">Akce</th>
          </tr>
        </thead>
        <tbody>
          {orders.map(order => {
            const stateStyle = getStateStyle(order.state);
            const isCanceled = order.state === 'CANCELED';
            return (
              <tr key={order.id}>
                <td className="text-nowrap">
                  <div className="fw-semibold">{order.number || order.id}</div>
                  <div className="small-muted">{formatDate(order.date_of_creation)}</div>
                </td>
                <td><MultiItemCell items={order.media} nameKey="name" /></td>
                <td><MultiItemCell items={order.projects} nameKey="name" /></td>
                <td className="inzeraty-cell"><RatecardItemsCell items={order.ratecard_items} /></td>
                <td>{order.responsible_person_name || <span className="text-muted">—</span>}</td>
                <td>
                  <span className="badge badge-stav" style={{ backgroundColor: stateStyle.bg, color: stateStyle.color }}>
                    {order.state_label || stateStyle.label}
                  </span>
                </td>
                <td className={`text-end fw-semibold text-nowrap ${isCanceled ? 'text-decoration-line-through text-muted' : ''}`}>
                  {formatPrice(order.total_price)} {order.currency || 'CZK'}
                </td>
                <td className="text-end">
                  <Button variant="outline-primary" className="btn-action" title="Detail objednávky" onClick={() => onOpen(order)}>
                    <i className="bi bi-eye"></i>
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
}

// Údaje z company_info s vlastním ovládacím prvkem ve formuláři. Zbytek se
// vypisuje genericky pod "Doplňkové informace". Drženo shodně s CustomerForm.
const HLAVNI_INFO = ['ID1', 'ID2', 'MaturityDay', 'CommunicationMethod', 'BankAccountNumber', 'IBAN'];

const KOMUNIKACE = [
  { value: 'EMAIL', label: 'E-mailem' },
  { value: 'POST', label: 'Poštou' },
  { value: 'EXTERNAL_SYSTEM', label: 'Externí systém' },
];

// Odpovědnost „Faktury a upomínky" — podle ní se hledá adresát faktury,
// takže bez e-mailu je takový kontakt k ničemu (stejné pravidlo hlídá backend).
const ODPOVEDNOST_FAKTURACE = 'Invoicing';

// Formát ověřujeme jen volně — jde o zachycení překlepů, ne o jeden povinný
// tvar (telefon může být zahraniční, s předvolbou i bez).
const jeEmail = (hodnota) => /^[^\s@]+@[^\s@]+\.[A-Za-z]{2,}$/.test(String(hodnota || '').trim());

const jeTelefon = (hodnota) => {
  const cisto = String(hodnota || '').replace(/[\s()/.-]/g, '');
  return cisto === '' || /^(\+|00)?\d{9,15}$/.test(cisto);
};

/**
 * Kdy událost končí — když konec není vyplněný, bere se začátek.
 * Formát z formuláře ("2026-10-01T23:55") i z API ("…+02:00") parsuje Date správně.
 */
const konecUdalosti = (udalost) => udalost.end_time || udalost.start_time || '';

const udalostJesteNeskoncila = (udalost) => {
  const konec = konecUdalosti(udalost);
  return konec !== '' && new Date(konec) > new Date();
};

/**
 * Stav události. Dřív se odvozoval jen z příznaku „dokončeno", takže se
 * i dávno proběhlá událost tvářila jako naplánovaná.
 */
function StavUdalosti({ udalost }) {
  if (udalost.is_completed) return <Badge bg="success">proběhlo</Badge>;
  if (udalostJesteNeskoncila(udalost)) return <Badge bg="warning">naplánováno</Badge>;
  // Přes 50 000 událostí z importu má příznak nenastavený — proto nenápadně.
  return <Badge bg="light" text="dark" className="border">nedokončeno</Badge>;
}

/** Odpovědnosti kontaktu jako odznaky — fakturační je zvýrazněná. */
function OdpovednostiKontaktu({ kontakt }) {
  const seznam = kontakt.responsibilities || [];
  if (seznam.length === 0) return <span className="text-muted">—</span>;

  return (
    <div className="d-flex flex-wrap gap-1">
      {seznam.map(r => (
        <Badge
          key={r.id}
          bg={r.id === ODPOVEDNOST_FAKTURACE ? 'primary' : 'light'}
          text={r.id === ODPOVEDNOST_FAKTURACE ? undefined : 'dark'}
          className={r.id === ODPOVEDNOST_FAKTURACE ? '' : 'border'}
        >
          {r.label || r.id}
        </Badge>
      ))}
    </div>
  );
}

// Kontakt, na který umí systém poslat fakturu.
const jeFakturacniKontakt = (kontakt) =>
  (kontakt.responsibilities || []).some(r => r.id === ODPOVEDNOST_FAKTURACE)
  && String(kontakt.email || '').trim() !== '';

// Typ vazby na obchodníka. Klient zatím typy nerozlišuje, takže se nevybírá
// a nové vazby vznikají vždycky jako „Account Manager"; starší data mají
// i „Manager" / „OTHER" a ty se při editaci zachovají (chip je vypíše v závorce).
const TYP_VAZBY = 'Account Manager';

// Klíč pro předání „po založení firmy rovnou otevři kontakt" z CustomerForm.
// Jednorázový — čte se při otevření detailu a hned se maže, ať se modal
// neotevírá znovu při obnovení tabu.
export const KLIC_NOVY_KONTAKT = 'regvyd:novy-kontakt-pro';

// Ikona a barva podle druhu zásahu — ať jde výpis číst pohledem.
const AKCE_STYL = {
  create: { ikona: 'bi-plus-circle-fill', barva: 'text-success' },
  update: { ikona: 'bi-pencil-fill', barva: 'text-primary' },
  delete: { ikona: 'bi-trash-fill', barva: 'text-danger' },
};

const casZmeny = (hodnota) => {
  const d = new Date(String(hodnota || '').replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  const p2 = (n) => String(n).padStart(2, '0');
  return `${p2(d.getDate())}.${p2(d.getMonth() + 1)}.${d.getFullYear()} ${p2(d.getHours())}:${p2(d.getMinutes())}`;
};

/**
 * Výpis historie. Řádek je jednou větou, detail změn se rozbalí kliknutím —
 * u záznamu s deseti změněnými poli by jinak nebylo vidět nic jiného.
 */
function VypisHistorie({ zaznamy, rozbaleny, onRozbalit }) {
  return (
    <div className="zmeny-list">
      {zaznamy.map((z) => {
        const styl = AKCE_STYL[z.action] || AKCE_STYL.update;
        const otevreny = rozbaleny === z.id;
        return (
          <div key={z.id} className={`zmena-row${otevreny ? ' otevrena' : ''}`}>
            <button
              type="button"
              className="zmena-hlava"
              onClick={() => z.change_count > 0 && onRozbalit(z.id)}
              aria-expanded={otevreny}
              disabled={z.change_count === 0}
            >
              <i className={`bi ${styl.ikona} ${styl.barva} zmena-ikona`} aria-hidden="true" />
              <span className="zmena-popis">{z.title}</span>
              <span className="zmena-meta">
                {z.user_name} · {casZmeny(z.created_at)}
              </span>
              {z.change_count > 0 && (
                <i className={`bi bi-chevron-${otevreny ? 'down' : 'right'} zmena-sipka`} aria-hidden="true" />
              )}
            </button>

            {otevreny && (
              <table className="zmena-detail">
                <tbody>
                  {z.changes.map((c, i) => (
                    <tr key={`${z.id}-${c.field}-${i}`}>
                      <th>{c.label}</th>
                      <td className="zmena-pred">{c.before}</td>
                      <td className="zmena-sip"><i className="bi bi-arrow-right" aria-hidden="true" /></td>
                      <td className="zmena-po">{c.after}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

function CustomerDetail({ customerId, openTab }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enums, setEnums] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Historie změn (change_log)
  const [historie, setHistorie] = useState([]);
  const [historieCelkem, setHistorieCelkem] = useState(0);
  const [historieNacita, setHistorieNacita] = useState(false);
  const [rozbaleny, setRozbaleny] = useState(null);
  const [showHistorieModal, setShowHistorieModal] = useState(false);

  // Edit company modal
  const [showEditCompany, setShowEditCompany] = useState(false);
  // Doplňkových údajů je 17 a většinou prázdných — v modalu jsou schované pod chevronem.
  const [doplnkoveOtevrene, setDoplnkoveOtevrene] = useState(false);
  const [companyForm, setCompanyForm] = useState({});
  const [infoForm, setInfoForm] = useState({});

  // Full-list modals
  const [showPoznamkyModal, setShowPoznamkyModal] = useState(false);
  const [showLideModal, setShowLideModal] = useState(false);
  const [showAdresyModal, setShowAdresyModal] = useState(false);
  const [showUdalostiModal, setShowUdalostiModal] = useState(false);
  const [showTagyModal, setShowTagyModal] = useState(false);
  const [showObjednavkyModal, setShowObjednavkyModal] = useState(false);
  const [objednavky, setObjednavky] = useState({ rows: [], total: 0, loading: false });
  const [vsechnyObjednavky, setVsechnyObjednavky] = useState({ rows: [], loading: false });

  // Edit modals
  const [showAddressEdit, setShowAddressEdit] = useState(false);
  const [showContactEdit, setShowContactEdit] = useState(false);
  const [showEventEdit, setShowEventEdit] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [addressForm, setAddressForm] = useState({});
  const [contactForm, setContactForm] = useState({});
  const [contactErrors, setContactErrors] = useState({});
  const [eventForm, setEventForm] = useState({});
  const [eventChyba, setEventChyba] = useState(null);

  // Detail kontaktní osoby (oko) — zodpovědnosti, obchodníci, poznámky
  const [showContactDetail, setShowContactDetail] = useState(false);
  const [detailContact, setDetailContact] = useState(null);
  const [detailNotes, setDetailNotes] = useState([]);
  const [detailRels, setDetailRels] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [detailDirty, setDetailDirty] = useState(false);
  const [noteForm, setNoteForm] = useState({ id: null, note: '', note_date: '' });
  const [showNoteEdit, setShowNoteEdit] = useState(false);
  const [addingResp, setAddingResp] = useState(false);

  // Vazby na obchodníky — sdílený modal pro detail i editaci kontaktu
  const [contactRels, setContactRels] = useState([]);
  const [showRelEdit, setShowRelEdit] = useState(false);
  const [relTarget, setRelTarget] = useState(null); // contact id, nebo 'new' u nezaloženého kontaktu
  const [relForm, setRelForm] = useState({ id: null, tempId: null, related_person_id: '', related_label: '', type: TYP_VAZBY });

  // Spinner přes celou stránku patří jen k prvnímu načtení (loading startuje
  // jako true). Při obnovách po uložení by jen probliklo prázdno a stránka
  // by „poskočila".
  const fetchDetail = useCallback(async () => {
    try {
      const [result, enumsResult] = await Promise.all([
        api.getCompany(customerId),
        api.getCrmEnums(),
      ]);
      if (result.data) {
        setCustomer(result.data);
      }
      if (enumsResult.data) {
        setEnums(enumsResult.data);
      }
    } catch (err) {
      console.error('Failed to fetch company detail:', err);
      setError('Nepodařilo se načíst detail zákazníka.');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Objednávky přes výpis objednávek (média, projekty, inzeráty, obchodník) — zvlášť, ať nezdržují detail.
  const nactiObjednavky = useCallback(async () => {
    if (!customerId) return;
    setObjednavky(prev => ({ ...prev, loading: true }));
    try {
      const res = await api.getOrders({ customer: customerId, limit: OBJEDNAVKY_V_BLOKU, sort: 'date_of_creation', order: 'DESC' });
      setObjednavky({ rows: res.data || [], total: res.pagination?.total ?? (res.data || []).length, loading: false });
    } catch (err) {
      console.error('Failed to fetch customer orders:', err);
      setObjednavky({ rows: [], total: 0, loading: false });
    }
  }, [customerId]);

  useEffect(() => {
    nactiObjednavky();
  }, [nactiObjednavky]);

  useEffect(() => {
    const handler = () => nactiObjednavky();
    window.addEventListener('orders:refresh', handler);
    return () => window.removeEventListener('orders:refresh', handler);
  }, [nactiObjednavky]);

  const otevriVsechnyObjednavky = async () => {
    setShowObjednavkyModal(true);
    setVsechnyObjednavky({ rows: [], loading: true });
    try {
      const res = await api.getOrders({ customer: customerId, limit: 500, sort: 'date_of_creation', order: 'DESC' });
      setVsechnyObjednavky({ rows: res.data || [], loading: false });
    } catch (err) {
      console.error('Failed to fetch customer orders:', err);
      setVsechnyObjednavky({ rows: [], loading: false });
    }
  };

  // Nová objednávka s předvyplněným zákazníkem z této karty
  const novaObjednavka = () => openTab(
    `order-new-${customerId}`,
    'Nová objednávka',
    '/orders/new',
    'OrderForm',
    { customerId: Number(customerId), customerName: customer.company_name || '' }
  );

  const otevriObjednavku = (order) => {
    setShowObjednavkyModal(false);
    openTab(
      `order-edit-${order.id}`,
      `Objednávka ${order.number || order.id}`,
      `/orders/${order.id}/edit`,
      'OrderForm',
      { orderId: order.id }
    );
  };

  // Historie se načítá zvlášť — je to doplňkový panel a nemá zdržovat detail.
  const nactiHistorii = useCallback(async (limit = 8) => {
    if (!customerId) return;
    setHistorieNacita(true);
    try {
      const res = await api.getCompanyHistory(customerId, { limit });
      setHistorie(res.data || []);
      setHistorieCelkem(res.pagination?.total || 0);
    } catch (err) {
      console.warn('[CustomerDetail] historii se nepodařilo načíst:', err);
    } finally {
      setHistorieNacita(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId) nactiHistorii();
  }, [customerId, nactiHistorii]);

  // Každá úspěšná akce zobrazí hlášku — navěšením na ni se historie obnoví
  // po uložení čehokoliv, včetně vazeb a poznámek, které detail nepřenačítají.
  useEffect(() => {
    if (successMsg) nactiHistorii();
  }, [successMsg, nactiHistorii]);

  // Při přepnutí na jiného zákazníka ať se neukazují data toho předchozího —
  // tady spinner na místě je, na rozdíl od obnovy po uložení.
  useEffect(() => {
    setLoading(true);
    setCustomer(null);
  }, [customerId]);

  useEffect(() => {
    if (customerId) fetchDetail();
  }, [customerId, fetchDetail]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  // ==========================================
  // COMPANY EDIT
  // ==========================================
  const openEditCompany = () => {
    setCompanyForm({
      company_name: customer.company_name || '',
      secondary_name: customer.secondary_name || '',
      homepage_url: customer.homepage_url || '',
      state: customer.state || 'active',
      business_prohibited: customer.business_prohibited || false,
      liquidation: customer.liquidation || false,
      revenues: customer.revenues || '',
      note: customer.note || '',
    });
    setInfoForm(customer.info || {});
    setDoplnkoveOtevrene(false);
    setShowEditCompany(true);
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.updateCompany(customerId, { ...companyForm, info: infoForm });
      showSuccess('Údaje zákazníka uloženy.');
      setShowEditCompany(false);
      fetchDetail();
    } catch (err) {
      setError('Nepodařilo se uložit: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // ==========================================
  // ADDRESS CRUD
  // ==========================================
  const openAddressEdit = (addr = null) => {
    setEditingAddress(addr);
    setAddressForm(addr ? {
      type: addr.type || '', name: addr.name || '', street: addr.street || '',
      city: addr.city || '', postal_code: addr.postal_code || '', county: addr.county || '',
      region: addr.region || '', country_id: addr.country_id || '', phone: addr.phone || '',
      web: addr.web || '', note: addr.note || '', is_valid: addr.is_valid !== false,
    } : {
      type: 'Main', name: '', street: '', city: '', postal_code: '',
      county: '', region: '', country_id: 2, phone: '', web: '', note: '', is_valid: true,
    });
    setShowAddressEdit(true);
  };

  const handleSaveAddress = async () => {
    setSaving(true);
    try {
      if (editingAddress) {
        await api.updateAddress(editingAddress.id, addressForm);
      } else {
        await api.createAddress({ ...addressForm, company_id: customerId });
      }
      showSuccess(editingAddress ? 'Adresa aktualizována.' : 'Adresa vytvořena.');
      setShowAddressEdit(false);
      fetchDetail();
    } catch (err) { setError('Chyba: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteAddress = async (id) => {
    if (!await confirmDelete({ title: 'Smazat adresu?', message: 'Adresa se z firmy odstraní.' })) return;
    try { await api.deleteAddress(id); showSuccess('Smazáno.'); fetchDetail(); }
    catch (err) { setError(err.message); }
  };

  // ==========================================
  // CONTACT CRUD
  // ==========================================
  const openContactEdit = (c = null) => {
    setEditingContact(c);
    setContactForm(c ? {
      first_name: c.first_name || '', last_name: c.last_name || '', position: c.position || '',
      department: c.department || '', email: c.email || '', cell_phone: c.cell_phone || '',
      work_phone: c.work_phone || '', address_id: c.address_id || '', sex: c.sex || '',
      note: '', responsibilities: (c.responsibilities || []).map(r => r.id),
    } : {
      first_name: '', last_name: '', position: '', department: '', email: '',
      cell_phone: '', work_phone: '', address_id: '', sex: '', note: '', responsibilities: [],
    });
    // U existujícího kontaktu se vazby mění rovnou přes API, u nového se drží v paměti
    // a založí se až po vytvoření kontaktu.
    setContactRels(c ? (c.relationships || []) : []);
    setContactErrors({});
    setShowContactEdit(true);
  };

  // Po založení firmy se CustomerForm ptá, jestli rovnou přidat kontaktní osobu.
  useEffect(() => {
    if (!customerId || loading) return;
    if (Number(sessionStorage.getItem(KLIC_NOVY_KONTAKT)) !== Number(customerId)) return;
    sessionStorage.removeItem(KLIC_NOVY_KONTAKT);
    openContactEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, loading]);

  // Vrátí popis chyb formuláře kontaktu; prázdný objekt = v pořádku.
  const zkontrolujKontakt = (form) => {
    const chyby = {};
    const email = (form.email || '').trim();

    if (email !== '' && !jeEmail(email)) {
      chyby.email = 'E-mail nemá platný tvar (např. jmeno@domena.cz).';
    } else if (email === '' && (form.responsibilities || []).includes(ODPOVEDNOST_FAKTURACE)) {
      chyby.email = 'Kontakt pro faktury a upomínky musí mít e-mail — jinak nejde určit, kam poslat fakturu.';
    }

    if (!jeTelefon(form.cell_phone)) chyby.cell_phone = 'Číslo nemá platný tvar (např. +420 123 456 789).';
    if (!jeTelefon(form.work_phone)) chyby.work_phone = 'Číslo nemá platný tvar (např. +420 123 456 789).';

    return chyby;
  };

  /**
   * Po založení kontaktu nabídne přihlášenému uživateli, že se k osobě přiřadí
   * jako obchodník. Když se uživatel v kontaktních osobách nedohledá (účty
   * a kontaktní osoby spolu nemají vazbu), nebo už je přiřazený, mlčí.
   */
  const nabidniPrirazeniSebe = async (contactId) => {
    let ja = null;
    try {
      ja = (await api.getMyContactPerson()).data;
    } catch (err) {
      console.warn('[CustomerDetail] obchodníka k účtu se nepodařilo dohledat:', err);
    }
    if (!ja?.id) return;
    if (contactRels.some(r => Number(r.related_person_id) === Number(ja.id))) return;

    const jmeno = [ja.first_name, ja.last_name].filter(Boolean).join(' ').trim();
    const potvrzeno = await confirm({
      title: 'Kontakt je uložený',
      message: `Chcete se k této osobě rovnou přiřadit jako obchodník${jmeno ? ` (${jmeno})` : ''}?`,
      icon: 'bi-person-badge',
      confirmText: 'Přiřadit mě',
      cancelText: 'Teď ne',
    });
    if (!potvrzeno) return;

    try {
      await api.createContactRelationship({
        contact_person_id: contactId,
        related_person_id: ja.id,
        type: TYP_VAZBY,
      });
    } catch (err) {
      setError('Vazbu na obchodníka se nepodařilo založit: ' + err.message);
    }
  };

  const handleSaveContact = async () => {
    const chyby = zkontrolujKontakt(contactForm);
    setContactErrors(chyby);
    if (Object.keys(chyby).length > 0) return;

    let novyId = null;
    setSaving(true);
    try {
      if (editingContact) {
        await api.updateContact(editingContact.id, contactForm);
      } else {
        const res = await api.createContact({ ...contactForm, company_id: customerId });
        novyId = res.data?.id || null;
        // Vazby nasbírané ve formuláři založíme až teď, kdy má kontakt id
        for (const r of (novyId ? contactRels : [])) {
          await api.createContactRelationship({
            contact_person_id: novyId,
            related_person_id: r.related_person_id,
            type: r.type,
          });
        }
      }
      showSuccess(editingContact ? 'Kontakt aktualizován.' : 'Kontakt vytvořen.');
      setShowContactEdit(false);
      setContactRels([]);
    } catch (err) {
      setError('Chyba: ' + err.message);
      setSaving(false);
      return;
    }
    setSaving(false);

    // Až po zavření formuláře — kontakt je uložený, tak ať nad ním nevisí
    // rozpracovaný dialog s tlačítkem „Ukládám…".
    if (novyId) await nabidniPrirazeniSebe(novyId);
    fetchDetail();
  };

  const handleDeleteContact = async (id) => {
    if (!await confirmDelete({ title: 'Smazat kontakt?', message: 'Smaže se i s poznámkami a vazbami na obchodníky.' })) return;
    try {
      await api.deleteContact(id);
      showSuccess('Smazáno.');
      if (detailContact?.id === id) closeContactDetail();
      fetchDetail();
    } catch (err) { setError(err.message); }
  };

  // ==========================================
  // CONTACT DETAIL (oko) + POZNÁMKY
  // ==========================================
  const loadContactNotes = async (contactId) => {
    setNotesLoading(true);
    try {
      const res = await api.getContactNotes(contactId);
      setDetailNotes(res.data || []);
    } catch (err) {
      setError('Nepodařilo se načíst poznámky: ' + err.message);
    } finally {
      setNotesLoading(false);
    }
  };

  const openContactDetail = (c) => {
    setDetailContact(c);
    setDetailNotes(c.notes || []);
    setDetailRels(c.relationships || []);
    setNoteForm({ id: null, note: '', note_date: '' });
    setAddingResp(false);
    setShowContactDetail(true);
    loadContactNotes(c.id);
  };

  // Změny v detailu (poznámky, zodpovědnosti, vazby) se dějí bez refetche celé firmy
  // (ten by zavřel modal kvůli loading stavu), promítnou se až při zavření.
  const closeContactDetail = () => {
    setShowContactDetail(false);
    setDetailContact(null);
    setDetailNotes([]);
    setDetailRels([]);
    setAddingResp(false);
    closeNoteEdit();
    if (detailDirty) {
      setDetailDirty(false);
      fetchDetail();
    }
  };

  // ==========================================
  // ZODPOVĚDNOSTI (contact_person_responsibility)
  // ==========================================
  const respTypes = enums?.responsibilityTypes || [];
  const respLabel = (code) => respTypes.find(t => t.id === code)?.label || code;

  // Backend při update kontaktu přepíše celou sadu zodpovědností, posíláme tedy vždy celý seznam.
  const saveDetailResponsibilities = async (codes) => {
    if (!detailContact) return;
    try {
      await api.updateContact(detailContact.id, { responsibilities: codes });
      setDetailContact(prev => ({
        ...prev,
        responsibilities: codes.map(c => ({ id: c, label: respLabel(c) })),
      }));
      setDetailDirty(true);
    } catch (err) { setError('Chyba: ' + err.message); }
  };

  const addDetailResponsibility = (code) => {
    const current = (detailContact?.responsibilities || []).map(r => r.id);
    if (!code || current.includes(code)) return;
    setAddingResp(false);
    // Fakturační kontakt bez e-mailu nedává smysl — stejné pravidlo jako ve formuláři.
    if (code === ODPOVEDNOST_FAKTURACE && String(detailContact?.email || '').trim() === '') {
      setError('Kontakt pro faktury a upomínky musí mít e-mail — doplň ho v úpravě kontaktu.');
      return;
    }
    saveDetailResponsibilities([...current, code]);
  };

  const removeDetailResponsibility = (code) => {
    const current = (detailContact?.responsibilities || []).map(r => r.id);
    saveDetailResponsibilities(current.filter(c => c !== code));
  };

  // ==========================================
  // VAZBY NA OBCHODNÍKY (contact_person_relationship)
  // ==========================================
  const relName = (r) => [r.last_name, r.first_name].filter(Boolean).join(' ').trim();

  // Jména obchodníků navázaných na kontaktní osobu, pro výpis v tabulce Lidé.
  const obchodniciKontaktu = (kontakt) => {
    const jmena = (kontakt.relationships || [])
      .map(r => relName(r) || (r.related_person_external ? `ext. ${r.related_person_external}` : ''))
      .filter(Boolean);
    return jmena.length > 0 ? jmena.join(', ') : '—';
  };

  const searchContactPersons = useCallback(async (query) => {
    const res = await api.searchSalespeople(query);
    return (res.data || []).map(p => ({
      id: p.id,
      label: [
        [p.last_name, p.first_name].filter(Boolean).join(' ').trim() || `#${p.id}`,
        p.company_name,
      ].filter(Boolean).join(' — '),
    }));
  }, []);

  const refreshRelationships = async (contactId) => {
    try {
      const res = await api.getContactRelationships(contactId);
      const list = res.data || [];
      if (detailContact?.id === contactId) setDetailRels(list);
      if (editingContact?.id === contactId) setContactRels(list);
      setDetailDirty(true);
    } catch (err) { setError('Chyba: ' + err.message); }
  };

  const openRelCreate = (target) => {
    setRelTarget(target);
    setRelForm({ id: null, tempId: null, related_person_id: '', related_label: '', type: TYP_VAZBY });
    setShowRelEdit(true);
  };

  const openRelEdit = (r, target) => {
    setRelTarget(target);
    setRelForm({
      id: r.id || null,
      tempId: r.tempId || null,
      related_person_id: r.related_person_id || '',
      related_label: relName(r) || r.related_label || '',
      type: r.type || TYP_VAZBY,
    });
    setShowRelEdit(true);
  };

  const closeRelEdit = () => {
    setShowRelEdit(false);
    setRelTarget(null);
    setRelForm({ id: null, tempId: null, related_person_id: '', related_label: '', type: TYP_VAZBY });
  };

  const handleSaveRelationship = async () => {
    if (!relForm.related_person_id) return;
    setSaving(true);
    try {
      if (relTarget === 'new') {
        // Kontakt ještě neexistuje — vazbu držíme jen ve formuláři
        const entry = {
          tempId: relForm.tempId || `tmp-${Date.now()}`,
          related_person_id: relForm.related_person_id,
          related_label: relForm.related_label,
          type: relForm.type,
        };
        setContactRels(prev => relForm.tempId
          ? prev.map(r => (r.tempId === relForm.tempId ? entry : r))
          : [...prev, entry]);
      } else if (relForm.id) {
        await api.updateContactRelationship(relForm.id, {
          related_person_id: relForm.related_person_id,
          type: relForm.type,
        });
        showSuccess('Vazba upravena.');
        await refreshRelationships(relTarget);
      } else {
        await api.createContactRelationship({
          contact_person_id: relTarget,
          related_person_id: relForm.related_person_id,
          type: relForm.type,
        });
        showSuccess('Obchodník přiřazen.');
        await refreshRelationships(relTarget);
      }
      closeRelEdit();
    } catch (err) { setError('Chyba: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteRelationship = async (r, target) => {
    if (target === 'new') {
      setContactRels(prev => prev.filter(x => x.tempId !== r.tempId));
      return;
    }
    if (!await confirmDelete({ title: 'Odebrat vazbu na obchodníka?', message: 'Kontaktní osoba zůstane, zmizí jen přiřazení obchodníka.', confirmText: 'Odebrat' })) return;
    try {
      await api.deleteContactRelationship(r.id);
      showSuccess('Vazba odebrána.');
      await refreshRelationships(target);
    } catch (err) { setError('Chyba: ' + err.message); }
  };

  const handleSaveNote = async () => {
    const text = (noteForm.note || '').trim();
    if (!text || !detailContact) return;
    setSaving(true);
    try {
      if (noteForm.id) {
        await api.updateContactNote(noteForm.id, { note: text, note_date: noteForm.note_date || null });
        showSuccess('Poznámka upravena.');
      } else {
        await api.createContactNote({
          contact_person_id: detailContact.id,
          note: text,
          note_date: noteForm.note_date || null,
        });
        showSuccess('Poznámka přidána.');
      }
      closeNoteEdit();
      setDetailDirty(true);
      await loadContactNotes(detailContact.id);
    } catch (err) { setError('Chyba: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!await confirmDelete({ title: 'Smazat poznámku?' })) return;
    try {
      await api.deleteContactNote(noteId);
      showSuccess('Poznámka smazána.');
      setDetailDirty(true);
      if (noteForm.id === noteId) closeNoteEdit();
      await loadContactNotes(detailContact.id);
    } catch (err) { setError('Chyba: ' + err.message); }
  };

  // Datum ve formátu pro <input type="date"> (lokální, ne UTC z toISOString).
  const dnes = () => {
    const d = new Date();
    const p2 = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
  };

  const openNoteCreate = () => {
    setNoteForm({ id: null, note: '', note_date: dnes() });
    setShowNoteEdit(true);
  };

  const openNoteEdit = (n) => {
    setNoteForm({
      id: n.id,
      note: n.note || '',
      note_date: n.note_date ? String(n.note_date).replace(' ', 'T').substring(0, 10) : '',
    });
    setShowNoteEdit(true);
  };

  const closeNoteEdit = () => {
    setShowNoteEdit(false);
    setNoteForm({ id: null, note: '', note_date: '' });
  };

  const toggleResp = (code) => {
    setContactForm(p => {
      const r = p.responsibilities || [];
      return { ...p, responsibilities: r.includes(code) ? r.filter(x => x !== code) : [...r, code] };
    });
  };

  // ==========================================
  // EVENT CRUD
  // ==========================================
  const openEventEdit = (ev = null) => {
    setEditingEvent(ev);
    setEventForm(ev ? {
      subject: ev.subject || '', note: ev.note || '',
      start_time: ev.start_time ? ev.start_time.replace(' ', 'T').substring(0, 16) : '',
      end_time: ev.end_time ? ev.end_time.replace(' ', 'T').substring(0, 16) : '',
      all_day: ev.all_day || false, is_completed: ev.is_completed || false,
    } : {
      // Události se v drtivé většině zapisují až zpětně, proto rovnou dokončené.
      subject: '', note: '', start_time: `${dnes()}T${new Date().toTimeString().substring(0, 5)}`,
      end_time: '', all_day: false, is_completed: true,
    });
    setEventChyba(null);
    setShowEventEdit(true);
  };

  const handleSaveEvent = async () => {
    // Výchozí stav nové události je „dokončeno" (zapisují se hlavně zpětně),
    // u události, která teprve přijde, to ale nedává smysl.
    if (eventForm.is_completed && udalostJesteNeskoncila(eventForm)) {
      setEventChyba('Událost ještě neskončila, dokončená být nemůže — odškrtni „Dokončeno", nebo uprav čas.');
      return;
    }
    setEventChyba(null);

    setSaving(true);
    try {
      if (editingEvent) {
        await api.updateEvent(editingEvent.id, eventForm);
      } else {
        await api.createEvent({ ...eventForm, company_id: customerId });
      }
      showSuccess(editingEvent ? 'Událost aktualizována.' : 'Událost vytvořena.');
      setShowEventEdit(false);
      fetchDetail();
    } catch (err) { setError('Chyba: ' + err.message); }
    finally { setSaving(false); }
  };

  // Rychlá fajfka v řádku — ať se kvůli odškrtnutí nemusí otevírat formulář.
  const oznacUdalostDokoncenou = async (ev) => {
    try {
      await api.updateEvent(ev.id, { is_completed: true });
      showSuccess('Událost označena jako dokončená.');
      // Přepíšeme jen tu jednu událost — na nic jiného změna nesahá.
      setCustomer(prev => ({
        ...prev,
        events: (prev.events || []).map(e => (e.id === ev.id ? { ...e, is_completed: 1 } : e)),
      }));
    } catch (err) { setError('Chyba: ' + err.message); }
  };

  const handleDeleteEvent = async (id) => {
    if (!await confirmDelete({ title: 'Smazat událost?' })) return;
    try { await api.deleteEvent(id); showSuccess('Smazáno.'); fetchDetail(); }
    catch (err) { setError(err.message); }
  };

  // ==========================================
  // HELPERS
  // ==========================================
  const fmtDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  const fmtDateShort = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });
  };
  const stripHtml = (h) => { if (!h) return ''; const t = document.createElement('div'); t.innerHTML = h; return t.textContent || ''; };
  const getInfoLabel = (id) => enums?.companyInfoTypes?.find(t => t.id === id)?.label || id;

  // Ve starých datech jsou i kombinace ("POST,EMAIL"). Když firma takovou
  // hodnotu má, přidáme ji do nabídky, aby ji uložení nesmazalo.
  const komunikaceAktualni = infoForm.CommunicationMethod || '';
  const komunikaceVolby = !komunikaceAktualni || KOMUNIKACE.some(k => k.value === komunikaceAktualni)
    ? KOMUNIKACE
    : [...KOMUNIKACE, { value: komunikaceAktualni, label: komunikaceAktualni }];

  // ==========================================
  // RENDER
  // ==========================================
  if (loading) {
    return <Container fluid className="px-4"><div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Načítání...</span></div></div></Container>;
  }
  if (!customer) {
    return <Container fluid className="px-4"><div className="alert alert-warning mt-4">Zákazník nebyl nalezen.</div></Container>;
  }

  // Doplňkové údaje = všechno z company_info, co nemá vlastní pole ve formuláři.
  const doplnkoveTypy = (enums?.companyInfoTypes || []).filter(t => !HLAVNI_INFO.includes(t.id));
  const doplnkoveVyplnene = doplnkoveTypy.filter(t => String(infoForm[t.id] ?? '').trim() !== '').length;

  const addresses = customer.addresses || [];
  const contacts = customer.contacts || [];
  const events = customer.events || [];
  const categories = customer.categories || [];
  const accountManagers = customer.account_managers || [];
  const info = customer.info || {};
  const infoRecords = customer.info_records || [];
  const sortedEvents = [...events].sort((a, b) => new Date(b.start_time || 0) - new Date(a.start_time || 0));
  const tags = categories.filter(c => c.is_selected).map(c => c.name);

  return (
    <div className="app-content container-fluid">
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}

      {/* ===== HEADER ===== */}
      <div className="company-header-card">
        <div className="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <h4 className="mb-0">{customer.company_name}</h4>
              <Badge bg={customer.state === 'active' ? 'success' : 'secondary'}>
                {customer.state === 'active' ? 'Aktivní' : 'Neaktivní'}
              </Badge>
            </div>
            <div className="small-muted">
              {info.ID1 && <>IČO {info.ID1}</>}
              {info.ID2 && <> · DIČ {info.ID2}</>}
              {customer.secondary_name && <> · {customer.secondary_name}</>}
            </div>
            {customer.homepage_url && (
              <div className="small-muted">
                <a href={customer.homepage_url.startsWith('http') ? customer.homepage_url : `https://${customer.homepage_url}`} target="_blank" rel="noopener noreferrer">
                  {customer.homepage_url}
                </a>
              </div>
            )}
          </div>
          <div className="text-end">
            <Button size="sm" variant="outline-primary" onClick={openEditCompany}>
              <i className="bi bi-pencil me-1"></i>Upravit
            </Button>
            {customer.last_check_date && (
              <div className="small-muted mt-2">Naposledy změněno: {fmtDate(customer.last_check_date)}</div>
            )}
          </div>
        </div>
      </div>

      {/* Bez fakturačního kontaktu s e-mailem nejde firmě odeslat faktura — ať je to vidět hned. */}
      {!contacts.some(jeFakturacniKontakt) && (
        <Alert variant="warning" className="d-flex align-items-center justify-content-between gap-3 py-2">
          <div>
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Chybí kontakt pro fakturaci — firma nemá osobu s odpovědností „Faktury a upomínky" a vyplněným e-mailem,
            takže jí nepůjde odeslat faktura.
          </div>
          <Button size="sm" variant="outline-dark" className="text-nowrap" onClick={() => openContactEdit()}>
            <i className="bi bi-person-plus me-1"></i>Přidat kontakt
          </Button>
        </Alert>
      )}

      {/* ===== GRID ===== */}
      <div className="row g-3">

        {/* Obchodníci / Account manageři */}
        <div className="col-12 col-lg-6">
          <div className="card card-tile h-100">
            <div className="card-header d-flex align-items-center justify-content-between">
              <strong><i className="bi bi-person-badge me-2"></i>Obchodník ({accountManagers.length})</strong>
            </div>
            <div className="card-body">
              {accountManagers.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {accountManagers.map((m, i) => {
                    const name = [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
                    return (
                      <div key={m.id || i} className="border rounded px-3 py-2 bg-white">
                        <div className="fw-bold">
                          <i className="bi bi-person-circle me-1 text-primary"></i>
                          {name || <span className="text-muted">Neznámý (ext. {m.related_person_external})</span>}
                        </div>
                        {m.email && <div className="small-muted"><i className="bi bi-envelope me-1"></i>{m.email}</div>}
                        {m.type && <Badge bg="light" text="dark" className="mt-1 border">{m.type}</Badge>}
                      </div>
                    );
                  })}
                </div>
              ) : <span className="text-muted">Žádný přiřazený obchodník</span>}
            </div>
          </div>
        </div>

        {/* Poznámky */}
        <div className="col-12 col-lg-6">
          <div className="card card-tile h-100">
            <div className="card-header d-flex align-items-center justify-content-between">
              <strong><i className="bi bi-sticky me-2"></i>Poznámky</strong>
              <div className="card-actions">
                <button className="btn btn-light border btn-sm" onClick={() => setShowPoznamkyModal(true)} title="Zvětšit">
                  <i className="bi bi-arrows-fullscreen"></i>
                </button>
              </div>
            </div>
            <div className="card-body">
              <div>{customer.note || <span className="text-muted">Žádné poznámky</span>}</div>
              {info.CommunicationMethod && <div className="mt-2 small-muted">Komunikace: {info.CommunicationMethod}</div>}
              {info.MaturityDay && <div className="small-muted">Splatnost: {info.MaturityDay} dní</div>}
            </div>
          </div>
        </div>

        {/* Lidé */}
        <div className="col-12">
          <div className="card card-tile">
            <div className="card-header d-flex align-items-center justify-content-between">
              <strong><i className="bi bi-people me-2"></i>Lidé ({contacts.length})</strong>
              <div className="card-actions">
                <button className="btn btn-light border btn-sm" onClick={() => openContactEdit()} title="Přidat"><i className="bi bi-plus"></i></button>
                <button className="btn btn-light border btn-sm" onClick={() => setShowLideModal(true)} title="Zobrazit vše"><i className="bi bi-arrows-fullscreen"></i></button>
              </div>
            </div>
            <div className="card-body p-0">
              {contacts.length > 0 ? (
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr><th>Jméno</th><th>Pozice</th><th>E-mail</th><th>Telefon</th><th>Odpovědnosti</th><th>Obchodník</th><th className="text-end">Akce</th></tr>
                  </thead>
                  <tbody>
                    {contacts.slice(0, LIDE_V_BLOKU).map(p => (
                      <tr key={p.id}>
                        <td><strong>{p.first_name} {p.last_name}</strong></td>
                        <td>{p.position || '—'}</td>
                        <td>{p.email || '—'}</td>
                        <td className="text-nowrap">{p.cell_phone || p.work_phone || '—'}</td>
                        <td><OdpovednostiKontaktu kontakt={p} /></td>
                        <td>{obchodniciKontaktu(p)}</td>
                        <td className="text-end text-nowrap">
                          <Button size="sm" variant="outline-secondary" className="me-1" title="Detail" onClick={() => openContactDetail(p)}><i className="bi bi-eye"></i></Button>
                          <Button size="sm" variant="outline-primary" className="me-1" title="Upravit" onClick={() => openContactEdit(p)}><i className="bi bi-pencil"></i></Button>
                          <Button size="sm" variant="outline-danger" title="Smazat" onClick={() => handleDeleteContact(p.id)}><i className="bi bi-trash"></i></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="p-3 text-muted">Žádné kontakty</div>}
            </div>
          </div>
        </div>

        {/* Adresy */}
        <div className="col-12 col-lg-6">
          <div className="card card-tile h-100">
            <div className="card-header d-flex align-items-center justify-content-between">
              <strong><i className="bi bi-geo-alt me-2"></i>Adresy ({addresses.length})</strong>
              <div className="card-actions">
                <button className="btn btn-light border btn-sm" onClick={() => openAddressEdit()} title="Přidat"><i className="bi bi-plus"></i></button>
                <button className="btn btn-light border btn-sm" onClick={() => setShowAdresyModal(true)} title="Zobrazit vše"><i className="bi bi-arrows-fullscreen"></i></button>
              </div>
            </div>
            <div className="card-body">
              {addresses.length > 0 ? addresses.slice(0, 2).map(a => (
                <div key={a.id} className="address-block polozka-s-akcemi">
                  <div className="polozka-akce">
                    <button className="btn btn-light border btn-sm" title="Upravit" onClick={() => openAddressEdit(a)}>
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button className="btn btn-light border btn-sm text-danger" title="Smazat" onClick={() => handleDeleteAddress(a.id)}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                  <strong>{a.type_label || a.type || 'Adresa'}</strong><br />
                  {a.country_name || ''}<br />
                  {[a.street, a.postal_code, a.city].filter(Boolean).join(', ')}
                </div>
              )) : <div className="text-muted">Žádné adresy</div>}
            </div>
          </div>
        </div>

        {/* Události */}
        <div className="col-12 col-lg-6">
          <div className="card card-tile h-100">
            <div className="card-header d-flex align-items-center justify-content-between">
              <strong><i className="bi bi-calendar-event me-2"></i>Události ({events.length})</strong>
              <div className="card-actions">
                <button className="btn btn-light border btn-sm" onClick={() => openEventEdit()} title="Přidat"><i className="bi bi-plus"></i></button>
                <button className="btn btn-light border btn-sm" onClick={() => setShowUdalostiModal(true)} title="Zobrazit vše"><i className="bi bi-arrows-fullscreen"></i></button>
              </div>
            </div>
            <div className="card-body">
              {sortedEvents.length > 0 ? sortedEvents.slice(0, 2).map(ev => (
                <div key={ev.id} className="event-item polozka-s-akcemi">
                  <div className="polozka-akce">
                    {/* Dokončit jde jen to, co už proběhlo — stejné pravidlo jako ve formuláři. */}
                    {!ev.is_completed && !udalostJesteNeskoncila(ev) && (
                      <button className="btn btn-light border btn-sm text-success" title="Označit jako dokončené" onClick={() => oznacUdalostDokoncenou(ev)}>
                        <i className="bi bi-check-lg"></i>
                      </button>
                    )}
                    <button className="btn btn-light border btn-sm" title="Upravit" onClick={() => openEventEdit(ev)}>
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button className="btn btn-light border btn-sm text-danger" title="Smazat" onClick={() => handleDeleteEvent(ev.id)}>
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                  <strong>{ev.subject}</strong> · {fmtDate(ev.start_time)}
                  {ev.note && <> — {stripHtml(ev.note).substring(0, 80)}{stripHtml(ev.note).length > 80 ? '…' : ''}</>}
                  <span className="ms-2"><StavUdalosti udalost={ev} /></span>
                </div>
              )) : <div className="text-muted">Žádné události</div>}
            </div>
          </div>
        </div>

        {/* Tagy a kategorie */}
        <div className="col-12">
          <div className="card card-tile">
            <div className="card-header d-flex align-items-center justify-content-between">
              <strong><i className="bi bi-tags me-2"></i>Tagy a kategorie</strong>
              <div className="card-actions">
                <button className="btn btn-light border btn-sm" onClick={() => setShowTagyModal(true)} title="Zobrazit vše"><i className="bi bi-arrows-fullscreen"></i></button>
              </div>
            </div>
            <div className="card-body">
              {tags.length > 0 ? tags.map((t, i) => <Badge key={i} bg="secondary" className="me-1 mb-1">{t}</Badge>) : <span className="text-muted">Žádné kategorie</span>}
            </div>
          </div>
        </div>

        {/* Objednávky */}
        <div className="col-12">
          <div className="card card-tile">
            <div className="card-header d-flex align-items-center justify-content-between">
              <strong><i className="bi bi-cart me-2"></i>Objednávky ({objednavky.total})</strong>
              <div className="card-actions">
                <button className="btn btn-primary btn-sm" onClick={novaObjednavka} title="Nová objednávka pro tohoto zákazníka">
                  <i className="bi bi-plus-lg me-1"></i>Nová objednávka
                </button>
                {objednavky.total > objednavky.rows.length && <button className="btn btn-light border btn-sm" onClick={otevriVsechnyObjednavky} title="Zobrazit vše"><i className="bi bi-arrows-fullscreen"></i></button>}
              </div>
            </div>
            <div className={objednavky.rows.length > 0 ? 'card-body p-0' : 'card-body'}>
              {objednavky.loading && objednavky.rows.length === 0
                ? <div className="text-muted">Načítání…</div>
                : objednavky.rows.length > 0
                  ? <ZakaznikObjednavkyTable orders={objednavky.rows} onOpen={otevriObjednavku} />
                  : <div className="text-muted">Žádné objednávky</div>}
            </div>
          </div>
        </div>

        {/* Historie změn */}
        <div className="col-12">
          <div className="card card-tile">
            <div className="card-header d-flex align-items-center justify-content-between">
              <strong><i className="bi bi-clock-history me-2"></i>Historie změn ({historieCelkem})</strong>
              {historieCelkem > historie.length && (
                <div className="card-actions">
                  <button
                    className="btn btn-light border btn-sm"
                    onClick={() => { setShowHistorieModal(true); nactiHistorii(200); }}
                    title="Zobrazit vše"
                  >
                    <i className="bi bi-arrows-fullscreen"></i>
                  </button>
                </div>
              )}
            </div>
            <div className="card-body p-0">
              {historieNacita && historie.length === 0 ? (
                <div className="p-3 text-muted">Načítám…</div>
              ) : historie.length === 0 ? (
                <div className="p-3 text-muted">Zatím žádné změny.</div>
              ) : (
                <VypisHistorie
                  zaznamy={historie}
                  rozbaleny={rozbaleny}
                  onRozbalit={(id) => setRozbaleny(rozbaleny === id ? null : id)}
                />
              )}
            </div>
          </div>
        </div>

      </div>{/* /row */}

      {/* ===== FULL-LIST MODALS ===== */}

      {/* Historie modal */}
      <Modal show={showHistorieModal} onHide={() => setShowHistorieModal(false)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-clock-history me-2"></i>Historie změn ({historieCelkem})</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {historie.length === 0
            ? <div className="p-3 text-muted">Zatím žádné změny.</div>
            : (
              <VypisHistorie
                zaznamy={historie}
                rozbaleny={rozbaleny}
                onRozbalit={(id) => setRozbaleny(rozbaleny === id ? null : id)}
              />
            )}
        </Modal.Body>
      </Modal>

      {/* Poznámky modal */}
      <Modal show={showPoznamkyModal} onHide={() => setShowPoznamkyModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title><i className="bi bi-sticky me-2"></i>Poznámky</Modal.Title></Modal.Header>
        <Modal.Body>
          <div>{customer.note || 'Žádné poznámky'}</div>
          {infoRecords.length > 0 && (
            <div className="mt-3">
              <h6>Doplňkové informace</h6>
              {infoRecords.map(r => <div key={r.id}><strong>{getInfoLabel(r.info_type)}:</strong> {r.value || '—'}</div>)}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Lidé modal */}
      <Modal show={showLideModal} onHide={() => setShowLideModal(false)} size="xl" scrollable>
        <Modal.Header closeButton><Modal.Title><i className="bi bi-people me-2"></i>Lidé ({contacts.length})</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="mb-2"><Button size="sm" variant="primary" onClick={() => { setShowLideModal(false); openContactEdit(); }}><i className="bi bi-plus me-1"></i>Přidat kontakt</Button></div>
          <Table size="sm" hover>
            <thead className="table-light"><tr><th>Jméno</th><th>Pozice</th><th>E-mail</th><th>Telefon</th><th>Odpovědnosti</th><th>Obchodník</th><th className="text-end">Akce</th></tr></thead>
            <tbody>
              {contacts.map(p => (
                <tr key={p.id}>
                  <td><strong>{p.first_name} {p.last_name}</strong></td>
                  <td>{p.position || '—'}</td>
                  <td>{p.email || '—'}</td>
                  <td className="text-nowrap">{p.cell_phone || p.work_phone || '—'}</td>
                  <td><OdpovednostiKontaktu kontakt={p} /></td>
                  <td>{obchodniciKontaktu(p)}</td>
                  <td className="text-end text-nowrap">
                    <Button size="sm" variant="outline-secondary" className="me-1" title="Detail" onClick={() => { setShowLideModal(false); openContactDetail(p); }}><i className="bi bi-eye"></i></Button>
                    <Button size="sm" variant="outline-primary" className="me-1" title="Upravit" onClick={() => { setShowLideModal(false); openContactEdit(p); }}><i className="bi bi-pencil"></i></Button>
                    <Button size="sm" variant="outline-danger" title="Smazat" onClick={() => handleDeleteContact(p.id)}><i className="bi bi-trash"></i></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
      </Modal>

      {/* Adresy modal */}
      <Modal show={showAdresyModal} onHide={() => setShowAdresyModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title><i className="bi bi-geo-alt me-2"></i>Adresy ({addresses.length})</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="mb-2"><Button size="sm" variant="primary" onClick={() => { setShowAdresyModal(false); openAddressEdit(); }}><i className="bi bi-plus me-1"></i>Přidat adresu</Button></div>
          {addresses.map(a => (
            <div key={a.id} className="address-block d-flex justify-content-between align-items-start">
              <div>
                <strong>{a.type_label || a.type || 'Adresa'}</strong><br />
                {a.country_name || ''}<br />
                {[a.street, a.postal_code, a.city].filter(Boolean).join(', ')}
                {a.phone && <><br />Tel: {a.phone}</>}
                {a.note && <><br /><span className="small-muted">{a.note}</span></>}
              </div>
              <div>
                <Button size="sm" variant="outline-primary" className="me-1" onClick={() => { setShowAdresyModal(false); openAddressEdit(a); }}><i className="bi bi-pencil"></i></Button>
                <Button size="sm" variant="outline-danger" onClick={() => handleDeleteAddress(a.id)}><i className="bi bi-trash"></i></Button>
              </div>
            </div>
          ))}
        </Modal.Body>
      </Modal>

      {/* Události modal */}
      <Modal show={showUdalostiModal} onHide={() => setShowUdalostiModal(false)} size="xl" scrollable>
        <Modal.Header closeButton><Modal.Title><i className="bi bi-calendar-event me-2"></i>Události ({events.length})</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="mb-2"><Button size="sm" variant="primary" onClick={() => { setShowUdalostiModal(false); openEventEdit(); }}><i className="bi bi-plus me-1"></i>Přidat událost</Button></div>
          {sortedEvents.map(ev => (
            <div key={ev.id} className="event-item d-flex justify-content-between align-items-start">
              <div>
                <strong>{ev.subject}</strong> · {fmtDate(ev.start_time)}
                {ev.end_time && <> – {fmtDate(ev.end_time)}</>}
                <span className="ms-2"><StavUdalosti udalost={ev} /></span>
                {ev.note && <div className="small-muted mt-1">{stripHtml(ev.note)}</div>}
              </div>
              <div className="text-nowrap">
                {!ev.is_completed && !udalostJesteNeskoncila(ev) && (
                  <Button size="sm" variant="outline-success" className="me-1" title="Označit jako dokončené" onClick={() => oznacUdalostDokoncenou(ev)}><i className="bi bi-check-lg"></i></Button>
                )}
                <Button size="sm" variant="outline-primary" className="me-1" onClick={() => { setShowUdalostiModal(false); openEventEdit(ev); }}><i className="bi bi-pencil"></i></Button>
                <Button size="sm" variant="outline-danger" onClick={() => handleDeleteEvent(ev.id)}><i className="bi bi-trash"></i></Button>
              </div>
            </div>
          ))}
        </Modal.Body>
      </Modal>

      {/* Tagy modal */}
      <Modal show={showTagyModal} onHide={() => setShowTagyModal(false)} size="lg">
        <Modal.Header closeButton><Modal.Title><i className="bi bi-tags me-2"></i>Tagy a kategorie</Modal.Title></Modal.Header>
        <Modal.Body>
          {categories.length > 0 ? categories.map((c, i) => (
            <div key={i}>
              <Badge bg={c.is_selected ? 'secondary' : 'light'} text={c.is_selected ? 'white' : 'dark'} className="me-1 mb-1">{c.name}</Badge>
            </div>
          )) : <span className="text-muted">Žádné kategorie</span>}
        </Modal.Body>
      </Modal>

      {/* Objednávky modal */}
      <Modal show={showObjednavkyModal} onHide={() => setShowObjednavkyModal(false)} size="xl" scrollable>
        <Modal.Header closeButton><Modal.Title><i className="bi bi-cart me-2"></i>Objednávky ({objednavky.total})</Modal.Title></Modal.Header>
        <Modal.Body className="p-0">
          {vsechnyObjednavky.loading
            ? <div className="text-muted p-3">Načítání…</div>
            : <ZakaznikObjednavkyTable orders={vsechnyObjednavky.rows} onOpen={otevriObjednavku} />}
        </Modal.Body>
      </Modal>

      {/* ===== EDIT MODALS ===== */}

      {/* Edit Company */}
      <Modal show={showEditCompany} onHide={() => setShowEditCompany(false)} size="xl">
        <Modal.Header closeButton><Modal.Title>Upravit údaje firmy</Modal.Title></Modal.Header>
        <Modal.Body>
          <h6 className="mb-3">Základní údaje</h6>
          <Row className="g-3">
            <Col md={6}><Form.Group><Form.Label>Název firmy</Form.Label><Form.Control value={companyForm.company_name || ''} onChange={e => setCompanyForm(p => ({ ...p, company_name: e.target.value }))} /></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Sekundární název</Form.Label><Form.Control value={companyForm.secondary_name || ''} onChange={e => setCompanyForm(p => ({ ...p, secondary_name: e.target.value }))} /></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label>Web</Form.Label><Form.Control value={companyForm.homepage_url || ''} onChange={e => setCompanyForm(p => ({ ...p, homepage_url: e.target.value }))} /></Form.Group></Col>
            <Col md={2}><Form.Group><Form.Label>Stav</Form.Label><Form.Select value={companyForm.state || 'active'} onChange={e => setCompanyForm(p => ({ ...p, state: e.target.value }))}><option value="active">Aktivní</option><option value="inactive">Neaktivní</option></Form.Select></Form.Group></Col>
            <Col md={3}><Form.Check id="firma-zakaz-obchodu" type="checkbox" label="Zákaz obchodu" checked={companyForm.business_prohibited || false} onChange={e => setCompanyForm(p => ({ ...p, business_prohibited: e.target.checked }))} className="mt-4" /></Col>
            <Col md={3}><Form.Check id="firma-likvidace" type="checkbox" label="V likvidaci" checked={companyForm.liquidation || false} onChange={e => setCompanyForm(p => ({ ...p, liquidation: e.target.checked }))} className="mt-4" /></Col>
            <Col md={12}><Form.Group><Form.Label>Poznámka</Form.Label><Form.Control as="textarea" rows={3} value={companyForm.note || ''} onChange={e => setCompanyForm(p => ({ ...p, note: e.target.value }))} /></Form.Group></Col>
          </Row>
          <h6 className="mt-4 mb-3">Fakturační údaje</h6>
          <Row className="g-3">
            <Col md={3}><Form.Group><Form.Label className="small">IČ</Form.Label><Form.Control size="sm" value={infoForm.ID1 || ''} onChange={e => setInfoForm(p => ({ ...p, ID1: e.target.value }))} /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label className="small">DIČ</Form.Label><Form.Control size="sm" value={infoForm.ID2 || ''} onChange={e => setInfoForm(p => ({ ...p, ID2: e.target.value }))} /></Form.Group></Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Splatnost (dní)</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={0}
                  max={365}
                  placeholder="14"
                  value={infoForm.MaturityDay || ''}
                  onChange={e => setInfoForm(p => ({ ...p, MaturityDay: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Způsob zasílání</Form.Label>
                <Form.Select
                  size="sm"
                  value={infoForm.CommunicationMethod || ''}
                  onChange={e => setInfoForm(p => ({ ...p, CommunicationMethod: e.target.value }))}
                >
                  <option value="">—</option>
                  {komunikaceVolby.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}><Form.Group><Form.Label className="small">Číslo účtu</Form.Label><Form.Control size="sm" value={infoForm.BankAccountNumber || ''} onChange={e => setInfoForm(p => ({ ...p, BankAccountNumber: e.target.value }))} /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label className="small">IBAN</Form.Label><Form.Control size="sm" value={infoForm.IBAN || ''} onChange={e => setInfoForm(p => ({ ...p, IBAN: e.target.value }))} /></Form.Group></Col>
          </Row>

          <button
            type="button"
            className="btn btn-link p-0 border-0 align-baseline text-decoration-none mt-4"
            onClick={() => setDoplnkoveOtevrene(v => !v)}
            aria-expanded={doplnkoveOtevrene}
          >
            <i className={`bi bi-chevron-${doplnkoveOtevrene ? 'down' : 'right'} me-1`}></i>
            Doplňkové informace ({doplnkoveVyplnene > 0 ? `${doplnkoveVyplnene} z ${doplnkoveTypy.length} vyplněno` : doplnkoveTypy.length})
          </button>

          {doplnkoveOtevrene && (
            <Row className="g-3 mt-0">
              {doplnkoveTypy.map(t => (
                <Col md={4} key={t.id}><Form.Group><Form.Label className="small">{t.label}</Form.Label><Form.Control size="sm" value={infoForm[t.id] || ''} onChange={e => setInfoForm(p => ({ ...p, [t.id]: e.target.value }))} /></Form.Group></Col>
              ))}
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditCompany(false)}>Zrušit</Button>
          <Button variant="primary" onClick={handleSaveCompany} disabled={saving}>{saving ? 'Ukládám...' : 'Uložit'}</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Address */}
      <Modal show={showAddressEdit} onHide={() => setShowAddressEdit(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>{editingAddress ? 'Upravit adresu' : 'Nová adresa'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={4}><Form.Group><Form.Label>Typ</Form.Label><Form.Select value={addressForm.type || ''} onChange={e => setAddressForm(p => ({ ...p, type: e.target.value }))}><option value="">—</option>{enums?.addressTypes?.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}</Form.Select></Form.Group></Col>
            <Col md={8}><Form.Group><Form.Label>Ulice</Form.Label><Form.Control value={addressForm.street || ''} onChange={e => setAddressForm(p => ({ ...p, street: e.target.value }))} /></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label>Město</Form.Label><Form.Control value={addressForm.city || ''} onChange={e => setAddressForm(p => ({ ...p, city: e.target.value }))} /></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>PSČ</Form.Label><Form.Control value={addressForm.postal_code || ''} onChange={e => setAddressForm(p => ({ ...p, postal_code: e.target.value }))} /></Form.Group></Col>
            <Col md={5}><Form.Group><Form.Label>Země</Form.Label><Form.Select value={addressForm.country_id || ''} onChange={e => setAddressForm(p => ({ ...p, country_id: e.target.value ? parseInt(e.target.value) : null }))}><option value="">—</option>{enums?.countries?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</Form.Select></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label>Okres</Form.Label><Form.Control value={addressForm.county || ''} onChange={e => setAddressForm(p => ({ ...p, county: e.target.value }))} /></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label>Kraj</Form.Label><Form.Control value={addressForm.region || ''} onChange={e => setAddressForm(p => ({ ...p, region: e.target.value }))} /></Form.Group></Col>
            <Col md={4}><Form.Group><Form.Label>Telefon</Form.Label><Form.Control value={addressForm.phone || ''} onChange={e => setAddressForm(p => ({ ...p, phone: e.target.value }))} /></Form.Group></Col>
            <Col md={12}><Form.Group><Form.Label>Poznámka</Form.Label><Form.Control as="textarea" rows={2} value={addressForm.note || ''} onChange={e => setAddressForm(p => ({ ...p, note: e.target.value }))} /></Form.Group></Col>
            <Col md={4}><Form.Check id="adresa-platna" type="checkbox" label="Platná" checked={addressForm.is_valid !== false} onChange={e => setAddressForm(p => ({ ...p, is_valid: e.target.checked }))} /></Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddressEdit(false)}>Zrušit</Button>
          <Button variant="primary" onClick={handleSaveAddress} disabled={saving}>{saving ? 'Ukládám...' : 'Uložit'}</Button>
        </Modal.Footer>
      </Modal>

      {/* Detail kontaktní osoby */}
      <Modal show={showContactDetail} onHide={closeContactDetail} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-circle me-2"></i>
            {detailContact ? `${detailContact.first_name || ''} ${detailContact.last_name || ''}`.trim() || 'Kontaktní osoba' : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailContact && (
            <>
              <Table size="sm" className="contact-detail-table mb-3">
                <tbody>
                  <tr><th>Pozice</th><td>{detailContact.position || '—'}</td></tr>
                  <tr><th>Oddělení</th><td>{detailContact.department || '—'}</td></tr>
                  <tr><th>E-mail</th><td>{detailContact.email ? <a href={`mailto:${detailContact.email}`}>{detailContact.email}</a> : '—'}</td></tr>
                  <tr><th>Mobil</th><td>{detailContact.cell_phone || '—'}</td></tr>
                  <tr><th>Prac. telefon</th><td>{detailContact.work_phone || '—'}</td></tr>
                  <tr><th>Adresa</th><td>{detailContact.address_text || '—'}</td></tr>
                  <tr>
                    <th>Zodpovědnosti</th>
                    <td>
                      <div className="d-flex flex-wrap align-items-center gap-1">
                        {(detailContact.responsibilities || []).length > 0
                          ? detailContact.responsibilities.map(r => (
                            <Badge key={r.id} bg="secondary" className="chip">
                              {r.label}
                              <button type="button" className="chip-remove" title="Odebrat" onClick={() => removeDetailResponsibility(r.id)}>×</button>
                            </Badge>
                          ))
                          : <span className="text-muted me-1">Žádné</span>}
                        {addingResp ? (
                          <Form.Select
                            size="sm"
                            autoFocus
                            className="chip-select"
                            defaultValue=""
                            onChange={e => addDetailResponsibility(e.target.value)}
                            onBlur={() => setAddingResp(false)}
                          >
                            <option value="">— vyberte —</option>
                            {respTypes
                              .filter(t => !(detailContact.responsibilities || []).some(r => r.id === t.id))
                              .map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </Form.Select>
                        ) : (
                          respTypes.some(t => !(detailContact.responsibilities || []).some(r => r.id === t.id)) && (
                            <Button size="sm" variant="light" className="border py-0 px-2" title="Přidat zodpovědnost" onClick={() => setAddingResp(true)}>
                              <i className="bi bi-plus"></i>
                            </Button>
                          )
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Obchodníci</th>
                    <td>
                      <div className="d-flex flex-wrap align-items-center gap-1">
                        {detailRels.length > 0
                          ? detailRels.map(r => (
                            <Badge key={r.id} bg="light" text="dark" className="chip border">
                              <i className="bi bi-person-badge me-1 text-primary"></i>
                              {relName(r) || <span className="text-muted">Neznámý (ext. {r.related_person_external})</span>}
                              {r.type && r.type !== TYP_VAZBY && <span className="small-muted ms-1">({r.type})</span>}
                              <button type="button" className="chip-edit" title="Upravit vazbu" onClick={() => openRelEdit(r, detailContact.id)}>
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button type="button" className="chip-remove" title="Odebrat" onClick={() => handleDeleteRelationship(r, detailContact.id)}>×</button>
                            </Badge>
                          ))
                          : <span className="text-muted me-1">Žádní</span>}
                        <Button size="sm" variant="light" className="border py-0 px-2" title="Přiřadit obchodníka" onClick={() => openRelCreate(detailContact.id)}>
                          <i className="bi bi-plus"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                  <tr><th>Poslední událost</th><td>{detailContact.last_event_date ? fmtDate(detailContact.last_event_date) : '—'}</td></tr>
                </tbody>
              </Table>

              <div className="d-flex align-items-center justify-content-between mb-2">
                <strong>
                  <i className="bi bi-sticky me-2"></i>Poznámky ({detailNotes.length})
                  {notesLoading && <span className="small-muted ms-2">Načítám…</span>}
                </strong>
                <Button size="sm" variant="light" className="border" title="Přidat poznámku" onClick={openNoteCreate}>
                  <i className="bi bi-plus"></i>
                </Button>
              </div>

              <div className="contact-notes-list">
                {detailNotes.length > 0 ? detailNotes.map(n => (
                  <div key={n.id} className="contact-note d-flex justify-content-between align-items-start">
                    <div className="me-2">
                      <span className="small-muted me-2">{n.note_date ? fmtDateShort(n.note_date) : 'bez data'}</span>
                      {n.note}
                    </div>
                    <div className="text-nowrap">
                      <Button size="sm" variant="link" className="p-0 me-2" title="Upravit poznámku" onClick={() => openNoteEdit(n)}>
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button size="sm" variant="link" className="text-danger p-0" title="Smazat poznámku" onClick={() => handleDeleteNote(n.id)}>
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </div>
                )) : <div className="text-muted p-2">Žádné poznámky</div>}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-danger" onClick={() => handleDeleteContact(detailContact.id)} disabled={!detailContact}>
            <i className="bi bi-trash me-1"></i>Smazat kontakt
          </Button>
          <Button variant="outline-primary" onClick={() => { const c = detailContact; closeContactDetail(); openContactEdit(c); }} disabled={!detailContact}>
            <i className="bi bi-pencil me-1"></i>Upravit
          </Button>
          <Button variant="secondary" onClick={closeContactDetail}>Zavřít</Button>
        </Modal.Footer>
      </Modal>

      {/* Poznámka kontaktní osoby — nová / úprava */}
      <Modal show={showNoteEdit} onHide={closeNoteEdit} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{noteForm.id ? 'Upravit poznámku' : 'Nová poznámka'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Text poznámky</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  autoFocus
                  value={noteForm.note}
                  onChange={e => setNoteForm(p => ({ ...p, note: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Datum</Form.Label>
                <Form.Control
                  type="date"
                  value={noteForm.note_date}
                  onChange={e => setNoteForm(p => ({ ...p, note_date: e.target.value }))}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeNoteEdit}>Zrušit</Button>
          <Button variant="primary" onClick={handleSaveNote} disabled={saving || !(noteForm.note || '').trim()}>
            {saving ? 'Ukládám...' : 'Uložit'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Vazba na obchodníka — nová / úprava */}
      <Modal show={showRelEdit} onHide={closeRelEdit} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{relForm.id || relForm.tempId ? 'Upravit vazbu' : 'Přiřadit obchodníka'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group>
                <Form.Label>Obchodník</Form.Label>
                <SearchableSelect
                  value={relForm.related_person_id}
                  selectedLabel={relForm.related_label}
                  onSearch={searchContactPersons}
                  onChange={(id, opt) => setRelForm(p => ({
                    ...p,
                    related_person_id: id,
                    related_label: opt ? opt.label : '',
                  }))}
                  placeholder="Hledat podle jména nebo e-mailu…"
                  emptyLabel="— vyberte osobu —"
                  allowEmpty={false}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRelEdit}>Zrušit</Button>
          <Button variant="primary" onClick={handleSaveRelationship} disabled={saving || !relForm.related_person_id}>
            {saving ? 'Ukládám...' : 'Uložit'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Contact */}
      <Modal show={showContactEdit} onHide={() => setShowContactEdit(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>{editingContact ? 'Upravit kontakt' : 'Nový kontakt'}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}><Form.Group><Form.Label>Jméno</Form.Label><Form.Control value={contactForm.first_name || ''} onChange={e => setContactForm(p => ({ ...p, first_name: e.target.value }))} /></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Příjmení</Form.Label><Form.Control value={contactForm.last_name || ''} onChange={e => setContactForm(p => ({ ...p, last_name: e.target.value }))} /></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Pozice</Form.Label><Form.Control value={contactForm.position || ''} onChange={e => setContactForm(p => ({ ...p, position: e.target.value }))} /></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Oddělení</Form.Label><Form.Control value={contactForm.department || ''} onChange={e => setContactForm(p => ({ ...p, department: e.target.value }))} /></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Email{(contactForm.responsibilities || []).includes(ODPOVEDNOST_FAKTURACE) && <span className="text-danger"> *</span>}</Form.Label><Form.Control type="email" value={contactForm.email || ''} isInvalid={!!contactErrors.email} onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))} /><Form.Control.Feedback type="invalid">{contactErrors.email}</Form.Control.Feedback></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Mobil</Form.Label><Form.Control value={contactForm.cell_phone || ''} isInvalid={!!contactErrors.cell_phone} onChange={e => setContactForm(p => ({ ...p, cell_phone: e.target.value }))} /><Form.Control.Feedback type="invalid">{contactErrors.cell_phone}</Form.Control.Feedback></Form.Group></Col>
            <Col md={3}><Form.Group><Form.Label>Prac. tel.</Form.Label><Form.Control value={contactForm.work_phone || ''} isInvalid={!!contactErrors.work_phone} onChange={e => setContactForm(p => ({ ...p, work_phone: e.target.value }))} /><Form.Control.Feedback type="invalid">{contactErrors.work_phone}</Form.Control.Feedback></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Adresa</Form.Label><Form.Select value={contactForm.address_id || ''} onChange={e => setContactForm(p => ({ ...p, address_id: e.target.value ? parseInt(e.target.value) : null }))}><option value="">—</option>{addresses.map(a => <option key={a.id} value={a.id}>{a.type_label || a.type} — {a.street}, {a.city}</option>)}</Form.Select></Form.Group></Col>
            <Col md={6}><Form.Group><Form.Label>Pohlaví</Form.Label><Form.Select value={contactForm.sex || ''} onChange={e => setContactForm(p => ({ ...p, sex: e.target.value }))}><option value="">—</option><option value="Male">Muž</option><option value="Female">Žena</option></Form.Select></Form.Group></Col>
            <Col md={12}><Form.Group><Form.Label>Odpovědnosti</Form.Label>{/* `id` je potřeba, aby react-bootstrap svázal popisek s políčkem a dalo se klikat i na text */}
              <div>{enums?.responsibilityTypes?.map(rt => <Form.Check key={rt.id} id={`resp-${rt.id}`} inline type="checkbox" label={rt.label} checked={(contactForm.responsibilities || []).includes(rt.id)} onChange={() => toggleResp(rt.id)} />)}</div></Form.Group></Col>
            {/* Poznámka jen u nového kontaktu — u existujícího se spravují v detailu */}
            {!editingContact && (
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Poznámka</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={contactForm.note || ''}
                    onChange={e => setContactForm(p => ({ ...p, note: e.target.value }))}
                  />
                </Form.Group>
              </Col>
            )}

            {/* Vazby na obchodníky — u nového kontaktu se založí až po uložení */}
            <Col md={12}>
              <Form.Label className="d-flex align-items-center justify-content-between">
                <span>Obchodníci</span>
                <Button size="sm" variant="light" className="border py-0 px-2" title="Přiřadit obchodníka" onClick={() => openRelCreate(editingContact ? editingContact.id : 'new')}>
                  <i className="bi bi-plus"></i>
                </Button>
              </Form.Label>
              <div className="d-flex flex-wrap align-items-center gap-1">
                {contactRels.length > 0 ? contactRels.map(r => (
                  <Badge key={r.id || r.tempId} bg="light" text="dark" className="chip border">
                    <i className="bi bi-person-badge me-1 text-primary"></i>
                    {relName(r) || r.related_label || `#${r.related_person_id}`}
                    {r.type && r.type !== TYP_VAZBY && <span className="small-muted ms-1">({r.type})</span>}
                    <button type="button" className="chip-edit" title="Upravit vazbu" onClick={() => openRelEdit(r, editingContact ? editingContact.id : 'new')}>
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button type="button" className="chip-remove" title="Odebrat" onClick={() => handleDeleteRelationship(r, editingContact ? editingContact.id : 'new')}>×</button>
                  </Badge>
                )) : <span className="text-muted">Žádní obchodníci</span>}
              </div>
              {!editingContact && contactRels.length > 0 && (
                <Form.Text className="text-muted">Vazby se založí po uložení kontaktu.</Form.Text>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowContactEdit(false)}>Zrušit</Button>
          <Button variant="primary" onClick={handleSaveContact} disabled={saving}>{saving ? 'Ukládám...' : 'Uložit'}</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Event */}
      <Modal show={showEventEdit} onHide={() => setShowEventEdit(false)} size="lg">
        <Modal.Header closeButton><Modal.Title>{editingEvent ? 'Upravit událost' : 'Nová událost'}</Modal.Title></Modal.Header>
        <Modal.Body>
          {eventChyba && <Alert variant="warning" className="py-2">{eventChyba}</Alert>}
          <Row className="g-3">
            <Col md={12}><Form.Group><Form.Label>Předmět</Form.Label><Form.Control value={eventForm.subject || ''} onChange={e => setEventForm(p => ({ ...p, subject: e.target.value }))} /></Form.Group></Col>
            <Col md={5}><Form.Group><Form.Label>Začátek</Form.Label><Form.Control type="datetime-local" value={eventForm.start_time || ''} onChange={e => setEventForm(p => ({ ...p, start_time: e.target.value }))} /></Form.Group></Col>
            <Col md={5}><Form.Group><Form.Label>Konec</Form.Label><Form.Control type="datetime-local" value={eventForm.end_time || ''} onChange={e => setEventForm(p => ({ ...p, end_time: e.target.value }))} /></Form.Group></Col>
            <Col md={2} className="d-flex align-items-end"><Form.Check id="udalost-cely-den" type="checkbox" label="Celý den" checked={eventForm.all_day || false} onChange={e => setEventForm(p => ({ ...p, all_day: e.target.checked }))} /></Col>
            <Col md={12}><Form.Group><Form.Label>Poznámka</Form.Label><Form.Control as="textarea" rows={4} value={eventForm.note || ''} onChange={e => setEventForm(p => ({ ...p, note: e.target.value }))} /></Form.Group></Col>
            <Col md={6}>
              <Form.Check id="udalost-dokonceno" type="checkbox" label="Dokončeno" checked={eventForm.is_completed || false} onChange={e => setEventForm(p => ({ ...p, is_completed: e.target.checked, }))} />
              {eventForm.is_completed && udalostJesteNeskoncila(eventForm) && (
                <Form.Text className="text-warning">Událost podle zadaných časů ještě neskončila.</Form.Text>
              )}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEventEdit(false)}>Zrušit</Button>
          <Button variant="primary" onClick={handleSaveEvent} disabled={saving}>{saving ? 'Ukládám...' : 'Uložit'}</Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
}

export default CustomerDetail;
